// Keep-alive prompt ("TV auto switch-off", spec 2026-10-19 §3.2, §4.4). Shared cases C70–C76, C78
// live in src/store/__tests__/keepaliveStore.test.ts and the KeepaliveHost test (Android ports them).

export type KeepalivePhase = "CASTER" | "ALL";
export interface KeepalivePrompt { slotId: number; promptId: number; phase: KeepalivePhase; deadline: number; casterId: string | null }
export interface KeepaliveAccepted { slotId: number; promptId: number; by: string | null; nextPromptAt: number | null }

const int = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? Math.trunc(v) : null);
/** A user id: a non-empty string (32 hex or a ULID), taken as-is. */
const uid = (v: unknown): string | null => (typeof v === "string" && v !== "" ? v : null);
const obj = (v: unknown): Record<string, unknown> | null => (typeof v === "object" && v !== null ? (v as Record<string, unknown>) : null);

/** Spec §3.2: `phase` is always the string name. */
export function phaseOf(v: unknown): KeepalivePhase | null {
  return v === "CASTER" || v === "ALL" ? v : null;
}

/** A PROMPT event's `metadata.keepalive`, or GET queue's `keepalive` (no slotId/casterId: pass them). */
export function parsePrompt(v: unknown, slotId?: number, casterId?: string | null): KeepalivePrompt | null {
  const o = obj(v);
  if (!o) return null;
  const id = int(o.slotId) ?? slotId ?? null;
  const promptId = int(o.promptId);
  const deadline = int(o.deadline);
  const phase = phaseOf(o.phase);
  if (id == null || promptId == null || deadline == null || phase == null) return null;
  return { slotId: id, promptId, phase, deadline, casterId: uid(o.casterId) ?? casterId ?? null };
}

export function parseAccepted(v: unknown): KeepaliveAccepted | null {
  const o = obj(v);
  const slotId = int(o?.slotId);
  const promptId = int(o?.promptId);
  if (!o || slotId == null || promptId == null) return null;
  return { slotId, promptId, by: uid(o.by), nextPromptAt: int(o.nextPromptAt) };
}

/** Spec §4.4: CASTER → the caster only; ALL → every viewer of the slot, and the caster keeps theirs. */
export function promptApplies(p: KeepalivePrompt, me: string | null, watching: boolean): boolean {
  if (me != null && p.casterId === me) return true;
  return p.phase === "ALL" && watching && me != null;
}

/** Whole seconds left, rounded up, never below 0 (deadline and now both server ms). */
export function secondsLeft(deadline: number, serverNow: number): number {
  return Math.max(0, Math.ceil((deadline - serverNow) / 1000));
}
